import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Skeleton,
  Alert,
  AlertIcon,
  useColorModeValue,
  Card,
  CardHeader,
  CardBody,
  Text,
  Stack,
  Tag,
  Badge,
  Icon,
  HStack,
} from "@chakra-ui/react";
import { useAppContext } from "../../../context/AppContext";
import { fetchOrganizationInfo } from "../../../services/AdminServices/OrganizationsInfo";
import {
  OrganizationData,
  renderLicenseIcon,
  TenantLicense,
} from "../../../models/OrganizationData";
import { FiMail, FiPhone, FiMapPin, FiCalendar, FiUsers } from "react-icons/fi";
import "./organization.css";

const OrganizationDetails: React.FC = () => {
  const [organizationData, setOrganizationData] =
    useState<OrganizationData | null>(null);
  const { accessToken, userData } = useAppContext();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mutedText = useColorModeValue("gray.600", "gray.400");

  const getOrganizationData = async () => {
    
    setLoading(true);
    setError(null);
    try {
      if (accessToken) {
        const userMailId = userData?.office365MailId ?? "";
        const tenantID = userData?.tenantID || 0;
        const data = await fetchOrganizationInfo(
          accessToken,
          userMailId,
          tenantID
        );
        console.log("ORG DAta", data);
        setOrganizationData(data);
      } else {
        setError("Access token is missing.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch organization data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      getOrganizationData();
    }
  }, [accessToken]);

  // Function to map licenseType to name
  // const getLicenseName = (licenseType: string) => {
  //   switch (licenseType) {
  //     case 101:
  //       return "Basic";
  //     case 102:
  //       return "Pro";
  //     case 103:
  //       return "Enterprise";
  //     default:
  //       return "Unknown";
  //   }
  // };

  // Color mode values for light/dark themes
  const cardBg = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  if (loading) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={4} />
        <Skeleton height="200px" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md" mb={6}>
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  if (!organizationData) {
    return (
      <Alert status="info" borderRadius="md" mb={6}>
        <AlertIcon />
        No data available.
      </Alert>
    );
  }

  const getLicenseColor = (licenseType: number) => {
    switch (licenseType) {
      case 101:
        return "blue";
      case 102:
        return "green";
      case 103:
        return "purple";
      default:
        return "gray";
    }
  };

  return (
    <Box p={6}>
      {/* Organization Details Card */}
      <Card
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        mb={6}
      >
        <CardHeader>
          <Heading as="h2" size="lg" fontWeight="semibold">
            Organization Details
          </Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            {[
              { label: "Organization Name", value: organizationData.name },
              { label: "Organization ID", value: organizationData.tenantID },
              { label: "Temas App ID", value: organizationData.teamAppID },
              { label: "Description", value: organizationData.description },
              { label: "Phone Number", value: organizationData.phoneNumber },
              { label: "Email", value: organizationData.email },
              {
                label: "Status",
                value: organizationData.isActive ? "Active" : "Inactive",
              },
              { label: "Address", value: organizationData.address },
            ].map((item, index) => (
              <FormControl key={index} display="flex" alignItems="center">
                <FormLabel fontWeight="bold" minW="180px" mb={0}>
                  {item.label}:
                </FormLabel>
                <Input
                  type="text"
                  value={item.value}
                  readOnly
                  variant="unstyled"
                  fontWeight="medium"
                />
              </FormControl>
            ))}
          </VStack>
        </CardBody>
      </Card>

      {/* Tenant Licenses Card */}
      <Card
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        mb={6}
      >
        <CardHeader>
          <Heading as="h3" size="lg" fontWeight="semibold">
            License Information
          </Heading>
        </CardHeader>
        <CardBody>
          {organizationData.licenses.length > 0 ? (
            <Box overflowX="auto">
              <Table className="Table">
                <Thead>
                  <Tr>
                    <Th>License</Th>
                    <Th>Title</Th>
                    <Th>Start Date</Th>
                    <Th>End Date</Th>
                    <Th>Status</Th>
                    <Th>Credit</Th>
                    <Th isNumeric>Total</Th>
                    <Th isNumeric>Assigned</Th>
                    <Th isNumeric>Available</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {organizationData.licenses.map(
                    (license: TenantLicense, index: number) => {
                      // const startDate = new Date(license.licenseStartDate);
                      // const isPastStart = new Date() > startDate;

                      const today = new Date();
                      const startDate = new Date(license.licenseStartDate);
                      const endDate = new Date(license.licenseEndDate)


                      const isLicenceExpire = (startDate <= today && endDate >= today);

                      return (
                        <Tr key={index}>
                          <Td>
                            <HStack>
                              {renderLicenseIcon(license.licenceTypeName)}
                              <Tag
                                colorScheme={getLicenseColor(
                                  license.licenseType
                                )}
                              >
                                {license.licenceTypeName}
                              </Tag>
                            </HStack>
                          </Td>
                          <Td>{license.title}</Td>
                           <Td>
                            <HStack>
                              <Text fontSize="md">
                                <Icon as={FiCalendar} mr={2} />
                                {new Date(
                                  license.licenseStartDate
                                ).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                              </Text>
                            </HStack>
                          </Td> 

                          <Td>
                            {endDate.toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                            {today >= startDate && today <= endDate ? (
                              <Badge colorScheme="green" ml={2} mt={0} width="fit-content">
                                In Use
                              </Badge>
                            ) : today > endDate ? (
                              <Badge colorScheme="red" ml={2} mt={0} width="fit-content">
                                Expired
                              </Badge>
                            ) : (
                              <Badge colorScheme="yellow" ml={2} mt={0} width="fit-content">
                                Pending
                              </Badge>
                            )}
                          </Td>

                           {/*<Td>
                            <HStack>
                              <Text fontSize="md">
                                <Icon as={FiCalendar} mr={2} />
                                {new Date(
                                  license.licenseEndDate
                                ).toLocaleDateString()}
                              </Text>
                            </HStack>
                          </Td>*/}
                          <Td>
                            <span
                              className={`status-box ${license.isLicenseActive ? "active" : "inactive"
                                }`}
                            >
                              {license.isLicenseActive ? "Active" : "Inactive"}
                            </span>
                          </Td>
                          <Td>
                            {license.maxTokenPerMonth
                              ? Math.ceil(license.maxTokenPerMonth / 500)
                              : "-"}
                          </Td>
                          <Td isNumeric>{license.noOfUsers}</Td>
                          <Td isNumeric>{license.assignedLicenses}</Td>
                          <Td isNumeric>
                            <Text
                              fontWeight="bold"
                              color={
                                license.availableLicenses > 0
                                  ? "green.500"
                                  : "red.500"
                              }
                            >
                              {license.availableLicenses}
                            </Text>
                          </Td>
                        </Tr>
                      );
                    }
                  )}
                </Tbody>
              </Table>
            </Box>
          ) : (
            <Text color={mutedText}>
              No licenses are available for your organization.
            </Text>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};

export default OrganizationDetails;
